import { MultiAVPlayerDebugState } from "@kalyzee/kast-web-sdk/web-components";
import React from "react";
import { DebugDot, DebugPane, DebugPaneProps } from "./DebugPane";

import styles from "./DebugPane.module.css";

export interface MultiVideoPlayerDebugPaneProps extends Omit<DebugPaneProps, "children"> {
  state: MultiAVPlayerDebugState | null;
}

const readyStateLabel = (rs?: number) => {
  switch (rs) {
    case 0: return 'NOTHING';
    case 1: return 'META';
    case 2: return 'CURRENT';
    case 3: return 'FUTURE';
    case 4: return 'ENOUGH';
    default: return '—';
  }
};

const driftColorOf = (driftMs?: number) => {
  const abs = driftMs !== undefined ? Math.abs(driftMs) : undefined;
  return abs === undefined ? '#666' : abs > 1500 ? '#e74c3c' : abs > 300 ? '#f39c12' : '#2ecc71';
};

const formatDrift = (driftMs?: number) =>
  driftMs !== undefined ? `${driftMs > 0 ? '+' : ''}${Math.round(driftMs)}ms` : '—';

// Decoder badge for a webcodecs tile. 'auto' only says what we ASKED for — pairing it with the
// hardware-capability probe reads what actually runs: auto + hw supported ⇒ hardware (very
// likely); auto + hw unsupported ⇒ software for sure; 'software' ⇒ prefer-software was forced
// after a real hardware failure. Fallback <video> tiles decode inside the browser's MSE pipeline.
const decoderBadge = (t: Extract<MultiAVPlayerDebugState, { engine: 'webcodecs' }>['tiles'][number]) => {
  if (t.mode !== 'canvas') return { label: 'MSE', color: '#666', title: 'fallback <video> — decoder managed by the browser' };
  const stats = t.canvasStats;
  if (!stats) return { label: '—', color: '#666', title: '' };
  // '·gl' = frames presented through the WebGL path (GPU texture upload + GPU YUV→RGB); its
  // absence means the page fell back to the ImageBitmap pipeline (no WebGL, or its context died).
  const gl = (stats as { presentation?: string }).presentation === 'gl' ? '·gl' : '';
  if (stats.decoder === 'software') return { label: `SW${gl}`, color: '#f39c12', title: 'prefer-software (hardware decode failed on this page)' };
  if (stats.hwDecodeSupported === false) return { label: `SW·auto${gl}`, color: '#f39c12', title: 'auto, but hardware unsupported for this config → software' };
  if (stats.hwDecodeSupported === true) return { label: `HW${gl}`, color: '#2ecc71', title: `auto, hardware supported → hardware (very likely); presentation: ${gl ? 'WebGL' : 'bitmap pipeline'}` };
  return { label: `auto${gl}`, color: '#666', title: 'auto — hardware capability probe pending' };
};

// One table per engine — the two debug-state shapes share nothing but the clock header.

const WebCodecsTiles = ({ state }: { state: Extract<MultiAVPlayerDebugState, { engine: 'webcodecs' }> }) => {
  const mounted = state.tiles.filter(t => t.mounted);
  const unmountedCount = state.tiles.length - mounted.length;
  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr className={styles.headerRow}>
            <th>source</th>
            <th title="mounted / displayed / healthy">m/d/ok</th>
            <th title="canvas: decode state — video: readyState">state</th>
            <th title="video decoder: HW / SW (forced or auto-fallback) / MSE for <video> tiles">dec</th>
            <th>drift</th>
            <th title="canvas: decoded ahead — video: n/a">ahead</th>
            <th title="Web Audio pipeline: state, and scheduled-ahead when playing">audio</th>
          </tr>
        </thead>
        <tbody>
          {mounted.map((t, i) => {
            const name = t.source.name ?? t.source.id ?? `#${i}`;
            const key = t.source.id ?? `${name}-${i}`;
            // Canvas tiles report decode-ahead in seconds; the pipeline only holds a couple of
            // frames, so the thresholds are much tighter than the video buffered-ahead ones.
            const aheadMs = t.canvasStats ? t.canvasStats.decodeAheadS * 1000 : undefined;
            const aheadColor = aheadMs === undefined ? '#666' : aheadMs <= 0 ? '#e74c3c' : '#2ecc71';
            const stateLabel = t.mode === 'canvas'
              ? (t.canvasStats?.error ? 'ERROR' : t.canvasStats?.ended ? 'ENDED' : t.canvasStats ? 'DECODING' : '—')
              : readyStateLabel(t.readyState);
            const dec = decoderBadge(t);
            return (
              <tr key={key}>
                <td className={styles.sourceName}>{name}</td>
                <td>
                  <div className={styles.dots}>
                    <DebugDot on={t.mounted} title="mounted" />
                    <DebugDot on={t.display} title="display" />
                    <DebugDot on={!t.broken} title="healthy" />
                  </div>
                </td>
                <td className={styles.readyState}>{t.mode === 'canvas' ? `C·${stateLabel}` : `V·${stateLabel}`}</td>
                <td style={{ color: dec.color }} title={dec.title}>{dec.label}</td>
                <td style={{ color: driftColorOf(t.driftMs) }}>{formatDrift(t.driftMs)}</td>
                <td style={{ color: aheadColor }}>
                  {aheadMs !== undefined ? `${Math.round(aheadMs)}ms` : '—'}
                </td>
                <td style={{
                  color: t.audio === undefined ? '#666'
                    : t.audio.state === 'error' ? '#e74c3c'
                    : t.audio.state === 'no-audio' ? '#888'
                    : t.audio.active ? '#2ecc71' : '#f39c12',
                }} title={t.audio?.error}>
                  {t.audio === undefined ? '—'
                    : t.audio.active && t.audio.scheduledAheadS !== undefined ? `♪${t.audio.scheduledAheadS.toFixed(1)}s`
                    : t.audio.state}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {unmountedCount > 0 && (
        <div className={styles.unmounted}>+{unmountedCount} unmounted</div>
      )}
    </>
  );
};

const MediaElementVideos = ({ state }: { state: Extract<MultiAVPlayerDebugState, { engine: 'media-element' }> }) => {
  const mounted = state.videos.filter(v => v.mounted);
  const unmountedCount = state.videos.length - mounted.length;
  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr className={styles.headerRow}>
            <th>source</th>
            <th title="mounted / displayed / active">m/d/a</th>
            <th>readyState</th>
            <th>drift</th>
            <th>preload</th>
          </tr>
        </thead>
        <tbody>
          {mounted.map((v, i) => {
            const name = v.source.name ?? v.source.id ?? `#${i}`;
            const key = v.source.id ?? `${name}-${i}`;
            const preloadColor = v.bufferedAheadMs === undefined
              ? '#666'
              : v.bufferedAheadMs <= 0 ? '#e74c3c' : v.bufferedAheadMs < 1500 ? '#f39c12' : '#2ecc71';
            return (
              <tr key={key}>
                <td className={styles.sourceName}>{name}</td>
                <td>
                  <div className={styles.dots}>
                    <DebugDot on={v.mounted} title="mounted" />
                    <DebugDot on={v.display} title="display" />
                    <DebugDot on={v.active} title="active" />
                  </div>
                </td>
                <td className={styles.readyState}>{readyStateLabel(v.readyState)}</td>
                <td style={{ color: driftColorOf(v.drift) }}>{formatDrift(v.drift)}</td>
                <td style={{ color: preloadColor }}>
                  {v.bufferedAheadMs !== undefined ? `${Math.round(v.bufferedAheadMs)}ms` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {unmountedCount > 0 && (
        <div className={styles.unmounted}>+{unmountedCount} unmounted</div>
      )}
    </>
  );
};

export const MultiVideoPlayerDebugPane = ({ state, ...paneProps }: MultiVideoPlayerDebugPaneProps) => {
  if (!state) return null;

  return (
    <DebugPane {...paneProps}>
      <div className={styles.header}>
        <span>t={((state.currentTime) / 1000).toFixed(1)}s</span>
        <span style={{ color: state.playing ? '#2ecc71' : '#888' }}>
          {state.playing ? 'playing' : 'paused'}
        </span>
        <span style={{ color: state.engine === 'webcodecs' ? '#2ecc71' : '#f39c12' }}>
          {state.engine}
        </span>
        {state.engine === 'webcodecs' && (
          <span style={{ color: state.audioContextState === 'running' ? '#2ecc71' : '#f39c12' }}>
            audio:{state.audioContextState ?? 'off'}
          </span>
        )}
        {state.clockPausedForBuffering && (
          <span className={styles.buffering}>BUFFERING</span>
        )}
      </div>
      {state.engine === 'webcodecs' ? <WebCodecsTiles state={state} /> : <MediaElementVideos state={state} />}
    </DebugPane>
  );
};
